function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.showAllVariantFieldsets();

    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  showAllVariantFieldsets() {
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      fieldset.classList.remove('hidden');
      fieldset.style.display = 'block';
    });
  }

  updateSelectionMetadata({ target }) {
    const { tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.hasAttribute('selected'))
        ?.removeAttribute('selected');

      target.selectedOptions[0].setAttribute('selected', 'selected');


      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
document.addEventListener("change", function (e) {

  const label = e.target
    ?.closest(".product-form__input")
    ?.querySelector("label");

  if (!label) return;

  if (label.textContent.trim().toLowerCase() !== "brand") return;

  const brandValue = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const modelWrapper = document.querySelector(".phone-model-wrapper");
  const modelSelect = document.querySelector(".phone-model-select");

  if (!modelWrapper || !modelSelect) return;

  const brandModels = {
    apple: ["iPhone 6", "iPhone 6 Plus", "iPhone 6s", "iPhone 6s Plus", "iPhone 7", "iPhone 7 Plus", "iPhone 8", "iPhone 8 Plus", "iPhone X", "iPhone XS", "iPhone XS Max", "iPhone XR", "iPhone 11", "iPhone 11 Pro", "iPhone 11 Pro Max", "iPhone 12", "iPhone 12 Mini", "iPhone 12 Pro", "iPhone 12 Pro Max", "iPhone 13", "iPhone 13 Mini", "iPhone 13 Pro", "iPhone 13 Pro Max", "iPhone 14", "iPhone 14 Plus", "iPhone 14 Pro", "iPhone 14 Pro Max", "iPhone 15", "iPhone 15 Plus", "iPhone 15 Pro", "iPhone 15 Pro Max", "iPhone 16", "iPhone 16 Plus", "iPhone 16 Pro", "iPhone 16 Pro Max", "iPhone 16e", "iPhone 17", "iPhone 17 Pro", "iPhone 17 Pro Max", "iPhone SE 2020", "iPhone SE 2022", "iPhone Air" ],
    samsung: ["Galaxy A02s", "Galaxy A03s", "Galaxy A04", "Galaxy A05s", "Galaxy A06", "Galaxy A10", "Galaxy A10s", "Galaxy A14 4G", "Galaxy A14 5G", "Galaxy A15 5G", "Galaxy A16", "Galaxy A17", "Galaxy A22 4G", "Galaxy A22 5G", "Galaxy A23", "Galaxy A23 5G", "Galaxy A25 5G", "Galaxy A26", "Galaxy A30s", "Galaxy A31", "Galaxy A32 4G", "Galaxy A32 5G", "Galaxy A33 5G", "Galaxy A34", "Galaxy A34 5G", "Galaxy A35", "Galaxy A35 5G", "Galaxy A36", "Galaxy A40", "Galaxy A40s", "Galaxy A50", "Galaxy A50s", "Galaxy A51", "Galaxy A52", "Galaxy A52 5G", "Galaxy A52s 5G", "Galaxy A53 5G", "Galaxy A54", "Galaxy A54 5G", "Galaxy A55", "Galaxy A55 5G", "Galaxy A70", "Galaxy A70s", "Galaxy A71", "Galaxy A72", "Galaxy A73 5G", "Galaxy A81", "Galaxy A91", "Galaxy F02s", "Galaxy F06 5G", "Galaxy F14 5G", "Galaxy F15", "Galaxy F16", "Galaxy F22", "Galaxy F23 5G", "Galaxy F34 5G", "Galaxy F36", "Galaxy F41", "Galaxy F42 5G", "Galaxy F54 5G", "Galaxy F55", "Galaxy F56 5G", "Galaxy F62", "Galaxy M02s", "Galaxy M06", "Galaxy M10", "Galaxy M14", "Galaxy M15", "Galaxy M16", "Galaxy M21", "Galaxy M21 2021", "Galaxy M23 5G", "Galaxy M30", "Galaxy M30s", "Galaxy M31", "Galaxy M31 Prime", "Galaxy M32", "Galaxy M32 5G", "Galaxy M33 5G", "Galaxy M34 5G", "Galaxy M35", "Galaxy M36", "Galaxy M51", "Galaxy M52 5G", "Galaxy M53 5G", "Galaxy M54 5G", "Galaxy M55", "Galaxy M56", "Galaxy M62", "Galaxy Note 10", "Galaxy Note 10 Lite", "Galaxy Note 10 Plus", "Galaxy Note 20", "Galaxy Note 20 5G", "Galaxy Note 20 Ultra", "Galaxy S8", "Galaxy S8 Plus", "Galaxy S9", "Galaxy S10", "Galaxy S10e", "Galaxy S10 Lite", "Galaxy S10 Plus", "Galaxy S20", "Galaxy S20 FE", "Galaxy S20 FE 5G", "Galaxy S20 Plus", "Galaxy S20 Ultra", "Galaxy S21", "Galaxy S21 FE", "Galaxy S21 Plus", "Galaxy S21 Ultra", "Galaxy S22 5G", "Galaxy S22 Plus 5G", "Galaxy S22 Ultra 5G", "Galaxy S23", "Galaxy S23 FE", "Galaxy S23 Plus", "Galaxy S23 Ultra", "Galaxy S24", "Galaxy S24 FE", "Galaxy S24 Plus", "Galaxy S24 Ultra", "Galaxy S25", "Galaxy S25 FE", "Galaxy S25 Plus", "Galaxy S25 Ultra", "Galaxy S25 Edge", "Galaxy S25 Slim", "Galaxy Z Flip 3 5G", "Galaxy Z Flip 4", "Galaxy Z Flip 5", "Galaxy Z Flip 6", "Galaxy Z Flip 7", "Galaxy Z Fold 3 5G", "Galaxy Z Fold 4", "Galaxy Z Fold 5", "Galaxy Z Fold 6", "Galaxy Z Fold 7"],
    vivo: ["Vivo X100 Pro","Vivo X100","Vivo X90 Pro","Vivo X90","Vivo X80 Pro","Vivo X80","Vivo X70 Pro","Vivo X70","Vivo X60 Pro","Vivo X60","Vivo X50 Pro","Vivo X50","Vivo X30","Vivo X27","Vivo X21","Vivo X20","Vivo X200 Pro","Vivo V29 Pro","Vivo V29","Vivo V27 Pro","Vivo V27","Vivo V25 Pro","Vivo V25","Vivo V23 Pro","Vivo V23","Vivo V21","Vivo V20","Vivo V19","Vivo V17","Vivo V15 Pro","Vivo V15","Vivo V11 Pro","Vivo V11","Vivo Y200 5G","Vivo Y100 5G","Vivo Y100","Vivo Y95","Vivo Y93","Vivo Y91","Vivo Y90","Vivo Y83","Vivo Y81","Vivo Y75","Vivo Y73","Vivo Y72 5G","Vivo Y71","Vivo Y70","Vivo Y69","Vivo Y67","Vivo Y66","Vivo Y55","Vivo Y53","Vivo Y52","Vivo Y51","Vivo Y50","Vivo Y35","Vivo Y33s","Vivo Y32","Vivo Y31","Vivo Y30","Vivo Y21","Vivo Y20","Vivo Y20i","Vivo Y20A","Vivo Y16","Vivo Y15","Vivo Y12","Vivo Y11","Vivo Y10","Vivo S1","Vivo S1 Pro","Vivo S5","Vivo S7","Vivo S9","Vivo Z1 Pro","Vivo Z1x","Vivo Z5","Vivo Z6 5G","Vivo Z1i","Vivo T1 5G","Vivo T2 5G","Vivo T3 5G","Vivo T1 Pro","Vivo T2 Pro"
],
    oppo: ["Oppo Find X7 Ultra", "Oppo Find X7", "Oppo Find X6 Pro", "Oppo Find X6", "Oppo Find X5 Pro", "Oppo Find X5", "Oppo Find X3 Pro", "Oppo Find X3", "Oppo Reno11 Pro 5G", "Oppo Reno11 5G", "Oppo Reno10 Pro Plus 5G", "Oppo Reno10 Pro 5G", "Oppo Reno10 5G", "Oppo Reno8 Pro 5G", "Oppo Reno8 5G", "Oppo Reno7 Pro 5G", "Oppo Reno7 5G", "Oppo Reno6 Pro 5G", "Oppo Reno6 5G", "Oppo Reno5 Pro 5G", "Oppo Reno5 5G", "Oppo Reno4 Pro", "Oppo Reno4", "Oppo Reno3 Pro", "Oppo Reno3", "Oppo Reno2", "Oppo Reno", "Oppo F25 Pro 5G", "Oppo F23 5G", "Oppo F21 Pro 5G", "Oppo F19 Pro Plus 5G", "Oppo F19 Pro", "Oppo F19", "Oppo F17 Pro", "Oppo F17", "Oppo F15", "Oppo F11 Pro", "Oppo F11", "Oppo A79 5G", "Oppo A78 5G", "Oppo A77", "Oppo A76", "Oppo A74 5G", "Oppo A74", "Oppo A73", "Oppo A72", "Oppo A71", "Oppo A59", "Oppo A58", "Oppo A57", "Oppo A56 5G", "Oppo A55", "Oppo A54", "Oppo A53", "Oppo A52", "Oppo A35", "Oppo A31", "Oppo A17", "Oppo A16", "Oppo A15", "Oppo K12", "Oppo K11", "Oppo K10", "Oppo K10 5G", "Oppo K9 5G", "Oppo K7 5G", "Oppo K5"],
    realme: ["realme gt 5g", "realme x7 max 5g", "realme q3 pro 5g", "realme gt neo flash", "realme gt neo2t", "realme gt neo 2", "realme gt 2 pro", "realme 13 pro", "realme 13 pro plus", "realme p2 pro", "realme 9i 4g", "realme 9 pro 5g", "realme q5", "realme v25", "realme neo7x", "realme p3", "realme gt 7 pro", "realme 9 pro 5g", "realme v25", "realme gt 6t", "realme gt neo6", "realme gt 6", "realme neo7x", "realme p3", "realme gt neo 2", "realme q5 pro", "realme gt 2", "realme gt neo 3t", "realme gt neo 3", "realme gt 6", "realme 13 pro", "realme 10 pro plus 5g", "realme gt 7", "realme gt 7t", "realme 12 plus", "realme narzo 70 pro", "realme p1", "realme narzo 70", "realme p2 pro", "realme 11x 5g", "realme 11 5g", "realme narzo 60x 5g", "realme x7 international", "realme q2 pro international", "realme 14 pro", "realme 12", "realme 12x", "realme c65 5g", "realme narzo 70x", "realme 13", "realme gt neo 3", "realme 9 5g", "realme p3 pro", "realme 13 pro plus", "realme p3 pro", "realme 15", "realme 12", "realme 12x", "realme c65 5g", "realme narzo 70x", "realme 13", "realme 7 pro", "realme 11 pro", "realme narzo 60 pro", "realme 11 pro plus", " realme11 international ", "realme narzo 60", "realme p3 ultra", "realme p3 ultra", "realme gt neo 2", "realme gt neo 3t", "realme gt 2", "realme q5 pro", "realme 5", "realme 5s", "realme narzo 10", "realme 12 pro", "realme 12 pro plus", "realme p1 pro", "realme 12 pro", "realme 12 pro plus", "realme p1 pro", "realme 14 pro", "realme gt neo 3", "realme 14 pro", "realme 9 5g speed edition", "realme 9 pro plus 5g", "realme narzo 50 pro", "realme 9 4g", "realme 8 5g", "realme narzo 30 5g", "realme 8s 5g", "realme v13 5g", "realme q3i 5g", "realme q3 5g", "realme 14 pro plus", "realme gt 7", "realme gt 7t", "realme 8", "realme 8 pro", "realme 6", "realme 6s", "realme 6i", "realme 10 pro 5g", "realme 15", "realme 15 pro", "realme gt neo6", "realme gt 6", "realme gt neo 6 se", "realme gt 6t", "realme neo7x", "realme p3", "realme 9 5g", "realme 12 plus", "realme narzo 70 pro", "realme p1", "realme narzo 70", "realme 15 pro", "realme p3 pro", "realme 13", "realme p3 pro", "realme 12", "realme 12x", "realme c65 5g", "realme narzo 70x"],
    poco: ["poco f3 gt", "poco m6 pro 5g", "poco m2 pro", "poco f3", "poco f4 5g", "poco f6", "poco f6", "poco x4 pro 5g", "poco x6", "poco m5s", "poco f3 gt", "poco m4 pro 5g", "poco x2", "poco x6 pro", "poco f3", "poco f5 5g", "poco m4 pro 5g", "poco m5s", "poco f4 5g", "poco x3", "poco x3 pro", "poco x3", "poco x3 pro", "poco x3 nfc", "poco m2 pro", "poco x4 pro 5g", "poco x4 gt", "poco x5 5g", "poco m4 pro 5g", "poco m6 pro 5g", "poco x6 pro", "poco f6", "poco f6", "poco m3 pro 5g", "poco f4 5g", "poco x6", "poco x5 5g", "poco f5 5g", "poco f1", "poco x5", "poco x5 pro 5g", "poco x3", "poco x3 pro", "poco f3 gt", "poco x5"],
    nothing: ["nothing phone 2", "nothing phone 1", "nothing phone 2a", "nothing phone 2a plus", "nothing cmf phone 2 pro", "nothing phone 1", "nothing cmf phone 1", "nothing phone 3a pro", "nothing phone 2", "nothing cmf phone 2 pro", "nothing phone 3a", "nothing cmf phone 2 pro", "nothing phone 2", "nothing phone 3", "nothing phone 2a plus", "nothing phone 2a", "nothing phone 3", "nothing phone 2a", "nothing phone 2a plus", "nothing phone 3a pro", "nothing phone 3", "nothing phone 3", "nothing cmf phone 1", "nothing phone 2", "nothing phone 3a", "nothing phone 3a pro", "nothing phone 3a", "nothing phone 1", "nothing phone 1", "nothing phone 3a", "nothing cmf phone 1"],
    oneplus: ["oneplus 11r 5g", "oneplus nord 3 5g", "oneplus ace 2v", "oneplus nord 3 5g", "oneplus ace 2v", "oneplus 6t", "oneplus 12", "oneplus 11 5g", "oneplus 9rt", "oneplus 13s", "oneplus 13t", "oneplus 13r", "oneplus 7t", "oneplus 6t", "oneplus 11 5g", "oneplus 7t pro", "oneplus 8 pro", "oneplus 15", "oneplus nord 2", "oneplus 8t", "oneplus 7pro", "oneplus nord ce 2 lite 5g", "oneplus nord ce 2 lite 5g", "oneplus 8 pro", "oneplus 10r", "oneplus ace", "oneplus nord ce 3 lite", "oneplus nord ce 3 lite", "oneplus nord 2t 5g", "oneplus 8 pro", "oneplus 13r", "oneplus 9r", "oneplus nord 2t 5g", "oneplus nord ce5", "oneplus nord", "oneplus 8 pro", "oneplus nord 3", "oneplus 9r", "oneplus nord ce 2 5g", "oneplus 11r 5g", "oneplus nord ce 4", "oneplus nord ce 2 lite 5g", "oneplus 13", "oneplus nord ce 3 5g", "oneplus 12r", "oneplus 10r", "oneplus nord 2", "oneplus nord ce5", "oneplus nord ce 2 5g", "oneplus 7t pro", "oneplus nord ce 4 lite", "oneplus nord ce 3 5g", "oneplus nord n20 5g", "oneplus 11r 5g", "oneplus ace 2 5g", "oneplus 9", "oneplus 9 pro", "oneplus nord 2t 5g", "oneplus nord 5", "oneplus 9 pro", "oneplus 7 pro", "oneplus 13r", "oneplus 10 pro 5g", "oneplus nord", "oneplus nord ce 4", "oneplus nord 2", "oneplus nord", "oneplus 10t 5g", "oneplus 13r", "oneplus nord 4", "oneplus 7t pro", "oneplus 9", "oneplus 12", "oneplus nord n20 5g", "oneplus nord ce4 lite 5g", "oneplus nord ce 3 5g", "oneplus 9", "oneplus 8t", "oneplus 7", "oneplus 7", "oneplus 10r", "oneplus ace", "oneplus nord 4", "oneplus nord ce4 lite 5g", "oneplus 7 pro", "oneplus nord ce 4", "oneplus 9r", "oneplus 10t 5g", "oneplus nord ce 2 5g", "oneplus 12r", "oneplus 15", "oneplus nord ce 3 lite", "oneplus 13s", "oneplus nord ce4 lite 5g", "oneplus nord 4", "oneplus 9 pro", "oneplus 8", "oneplus 6", "oneplus 12r", "oneplus nord 5", "oneplus 6t", "oneplus 13", "oneplus 7t", "oneplus ace racing", "oneplus nord ce5", "oneplus 7", "oneplus nord ce 3 lite", "oneplus nord ce 2", "oneplus 7", "oneplus 8", "oneplus 13", "oneplus 10 pro 5g", "oneplus 10r", "oneplus ace 5g", "oneplus 11r 5g", "oneplus ace 2 5g", "oneplus 12", "oneplus 7t", "oneplus nord ce5", "oneplus 12", "oneplus nord 3 5g","oneplus ace 2v", "oneplus 9 pro", "oneplus nord 4", "oneplus 13s", "oneplus nord ce 2 lite 5g", "oneplus 10 pro 5g", "oneplus nord 5", "oneplus 15", "oneplus 6", "oneplus nord ce 5g", "oneplus 13", "oneplus 10t 5g", "oneplus nord ce 4", "oneplus 6t", "oneplus 7t pro", "oneplus 8", "oneplus 10t 5g" ],
    iqoo: ["iQOO 12 Pro","iQOO 12","iQOO 11 Pro","iQOO 11","iQOO 9 Pro","iQOO 9","iQOO 7 Legend","iQOO 7","iQOO Neo9 Pro","iQOO Neo9","iQOO Neo7 Pro","iQOO Neo7","iQOO Neo6","iQOO Neo5","iQOO Z9 5G","iQOO Z9x 5G","iQOO Z7 Pro 5G","iQOO Z7 5G","iQOO Z6 Pro 5G","iQOO Z6 5G","iQOO Z5 5G","iQOO Z3 5G","iQOO U5 5G","iQOO U3 5G","iQOO U1","iQOO 3","iQOO Pro 5G"
  ],
    motorola: ["motorola edge 30", "motorola edge 30 fusion", "motorola edge 30 ultra", "motorola edge 40", "motorola edge 40 neo", "motorola edge 50", "motorola edge 50 fusion", "motorola edge 50 pro", "motorola edge 50 ultra", "motorola edge 60 fusion", "motorola edge 60 pro", "motorola moto g45", "motorola moto g54", "motorola moto g64", "motorola moto g84", "motorola moto g85 5g", "motorola moto g86 power", "motorola g96"],
    google: ["google pixel 9a", "google pixel 7 pro", "google pixel 7a", "google pixel 8a", "google pixel 9", "google pixel 9 pro", "google pixel 9 pro", "google pixel 8 pro", "google pixel 9", "google pixel 9 pro", "google pixel 7", "google pixel 9a", "google pixel 6 pro", "google pixel 6a", "google pixel 6 pro", "google pixel 9a", "google pixel 8 pro", "google pixel 7", "google pixel 10", "google pixel 8a", "google pixel 10", "google pixel 6 pro", "google pixel 8 pro", "google pixel 7 pro", "google pixel 6a", "google pixel 6", "google pixel 6", "google pixel 7 pro", "google pixel 10 pro xl", "google pixel 9 pro", "google pixel 7", "google pixel 4", "google pixel 6a", "google pixel 10 pro xl", "google pixel 6", "google pixel 9 pro xl", "google pixel 10", "google pixel 9a", "google pixel 8a", "google pixel 9 pro xl", "google pixel 8a", "google pixel 7a", "google pixel 7a", "google pixel 10 pro", "google pixel 9", "google pixel 10 pro", "google pixel 8", "google pixel 10 pro xl", "google pixel 9", "google pixel 8", "google pixel 9 pro xl", "google pixel 7 pro", "google pixel 9 pro xl", "google pixel 7a", "google pixel 8 pro", "google pixel 6 pro", "google pixel 10 pro", "google pixel 6", "google pixel 8", "google pixel 8"],
    redmi: ["Redmi 6","Redmi 6A","Redmi 7","Redmi 7A","Redmi 8","Redmi 8A","Redmi 9","Redmi 9A","Redmi 9C","Redmi 9i","Redmi 9 Prime","Redmi 10","Redmi 10A","Redmi 10C","Redmi 11 Prime","Redmi 12","Redmi 12 5G","Redmi 13","Redmi 13C","Redmi 13C 5G","Redmi Note 7","Redmi Note 7 Pro","Redmi Note 8","Redmi Note 8 Pro","Redmi Note 9","Redmi Note 9 Pro","Redmi Note 9 Pro Max","Redmi Note 9S","Redmi Note 10","Redmi Note 10 Lite","Redmi Note 10 Pro","Redmi Note 10 Pro Max","Redmi Note 10S","Redmi Note 11","Redmi Note 11 Pro","Redmi Note 11 Pro Plus","Redmi Note 11S","Redmi Note 12","Redmi Note 12 5G","Redmi Note 12 Pro","Redmi Note 12 Pro Plus","Redmi Note 12S","Redmi Note 13","Redmi Note 13 5G","Redmi Note 13 Pro","Redmi Note 13 Pro Plus","Redmi K20","Redmi K20 Pro","Redmi K30","Redmi K30 Pro","Redmi K40","Redmi K40 Pro","Redmi K50","Redmi K50i","Redmi K50 Pro","Redmi K60","Redmi K60 Pro","Redmi A1","Redmi A1 Plus","Redmi A2","Redmi A2 Plus","Redmi Go","Redmi Y2","Redmi Y3","Redmi S2","Redmi Turbo 3"]
   };

  // 🔥 RESET MODEL EVERY TIME
  modelSelect.innerHTML = '<option value="">Select Model</option>';
  modelWrapper.style.display = "none";

  if (!brandModels[brandValue]) return;

  brandModels[brandValue].forEach(model => {

    const opt = document.createElement("option");
    opt.value = model;      
    opt.textContent = model;
    modelSelect.appendChild(opt);
  });

  modelWrapper.style.display = "block";
});
document.addEventListener('DOMContentLoaded', () => {

  const brandModelMap = {
    Apple: ["iPhone 13", "iPhone 14", "iPhone 15"],
    Samsung: ["Galaxy S23", "Galaxy S24", "Galaxy A54"]
  };

  const modelWrapper = document.getElementById('model-wrapper');
  const modelSelect = document.getElementById('model-select');

  if (!modelWrapper || !modelSelect) return;

  function updateModelDropdown(brand) {
    modelSelect.innerHTML = '<option value="">Select Model</option>';

    if (brandModelMap[brand]) {
      brandModelMap[brand].forEach(model => {
        const option = document.createElement('option');
        option.value = model;
        option.textContent = model;
        modelSelect.appendChild(option);
      });
      modelWrapper.style.display = 'block';
    } else {
      modelWrapper.style.display = 'none';
    }
  }

  // ✅ Dawn Variant Change Listener
  document.addEventListener('variant:change', function (event) {
    if (!event.detail || !event.detail.variant) return;

    const brand = event.detail.variant.options[1]; 
    // 👆 agar Brand 2nd dropdown hai

    updateModelDropdown(brand);
  });

});

document.addEventListener("change", function (e) {
  if (!e.target.matches(".product-form__input--dropdown select")) return;

  const selects = document.querySelectorAll(
    ".product-form__input--dropdown select"
  );

  // jab 2nd dropdown change ho
  if (selects.length >= 3 && e.target === selects[1]) {
    // force trigger 3rd dropdown refresh
    selects[2].dispatchEvent(new Event("change", { bubbles: true }));
  }
});
document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll("variant-selects")
    .forEach((variantSelect) => {
      // show all fieldsets
      variantSelect.querySelectorAll("fieldset").forEach((fs) => {
        fs.classList.remove("hidden");
        fs.style.display = "block";
      });

      // show all dropdowns
      variantSelect
        .querySelectorAll(".product-form__input--dropdown")
        .forEach((dd) => {
          dd.style.display = "block";
        });
    });
});

